import { DraggableItemList, SplitButton } from '@components'

import { Flex, NavItem, Space, Spacing, Text } from '@billy/design-system'
import countBy from 'lodash/countBy'
import { useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'

import { NEW_LINE_SHORTCUT } from '../../../../../../../../../../constants/shortcuts'
import { useFieldArray } from '../../../../../../../../../../hooks'
import { isMac } from '../../../../../../../../../../utils/isMac'
import { depositInvoiceEditorVariants } from '../../../../../../constants/variants/depositInvoiceEditorVariants'
import { invoiceEditorElementRestrictionsByVariant } from '../../../../../../constants/variants/invoiceEditorElementRestrictionsByVariant'
import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../../../../../../hooks/useInvoiceEditorVariant'
import { getDefaultDescriptionLine } from '../../../../../../utils/invoiceEditorForm/getDefaultDescriptionLine'
import { getDefaultRowLine } from '../../../../../../utils/invoiceEditorForm/getDefaultRowLine'
import { getDefaultSubtotalLine } from '../../../../../../utils/invoiceEditorForm/getDefaultSubtotalLine'
import { useUpdateDependentLineValues } from '../../hooks/useUpdateDependentLineValues'
import { DepositInvoiceLines } from './elements/DepositInvoiceLines'
import { InvoiceDiscounts } from './elements/InvoiceDiscounts'
import { InvoiceEditLinesColumns } from './elements/InvoiceEditLinesColumns'
import { InvoiceLine } from './elements/InvoiceLine'
import { InvoiceLinesHeader } from './elements/InvoiceLinesHeader'
import { AddLineItemActionId } from './enums/AddLineItemsActionId'

export const NEW_LINE_SHORTCUT_KEYS_LABEL = isMac ? '⌘+L' : 'Ctrl+L'

export const InvoiceLines = () => {
  const { t } = useTranslation()
  const { invoiceEditorForm, predefinedInvoiceValues, trackInvoiceEditor } = useInvoiceEditorForm()
  const { recalculateSubtotals } = useUpdateDependentLineValues()
  const { documentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()

  const variantConfig = invoiceEditorElementRestrictionsByVariant[invoiceEditorVariant]
  const isQuote = documentType === InvoiceDocumentType.Quote
  const areInvoiceLinesDisabled = variantConfig?.invoiceLineColumns?.restriction === 'disabled'
  const isDepositInvoice = depositInvoiceEditorVariants.includes(invoiceEditorVariant)

  const { control, getValues } = invoiceEditorForm

  const {
    fields: lines,
    append: addLine,
    move: moveLine,
    remove: removeLine,
    update: updateLine,
  } = useFieldArray({ control, name: 'invoiceLines' })

  const defaultRowLine = useMemo(() => getDefaultRowLine(predefinedInvoiceValues), [predefinedInvoiceValues])
  const defaultDescriptionLine = useMemo(() => getDefaultDescriptionLine(), [])

  const handleAddRowLineClick = useCallback(() => {
    addLine(defaultRowLine)
    trackInvoiceEditor('Form - Invoice lines - Add invoice row line button clicked')
  }, [addLine, defaultRowLine, trackInvoiceEditor])

  const handleAddRowLineShortcutPressed = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault()
      addLine(defaultRowLine)
      trackInvoiceEditor('Form - Invoice lines - Add invoice row line shortcut used')
    },
    [addLine, defaultRowLine, trackInvoiceEditor],
  )

  const handleAddDescriptionLineClick = useCallback(() => {
    addLine(defaultDescriptionLine)
    trackInvoiceEditor('Form - Invoice lines - Add invoice description line button clicked')
  }, [addLine, defaultDescriptionLine, trackInvoiceEditor])

  const handleAddSubtotalLineClick = useCallback(() => {
    const currentFormLines = getValues('invoiceLines') || []
    const defaultSubtotalLine = getDefaultSubtotalLine(currentFormLines)
    addLine(defaultSubtotalLine)
    trackInvoiceEditor('Form - Invoice lines - Add invoice subtotal line button clicked')
  }, [addLine, getValues, trackInvoiceEditor])

  const handleRemoveLineClick = useCallback(
    (index: number, lineType: InvoiceLineType) => () => {
      trackInvoiceEditor('Form - Invoice lines - Remove invoice line button clicked')
      const lineTypeCounts = countBy(lines, 'type')
      if (lineTypeCounts[InvoiceLineType.Row] === 1) {
        updateLine(index, defaultRowLine)
      } else {
        removeLine(index)
        recalculateSubtotals(true)
      }
    },
    [defaultRowLine, lines, recalculateSubtotals, removeLine, trackInvoiceEditor, updateLine],
  )

  const handleLineOrderChange = useCallback(
    (oldIndex: number, newIndex: number) => {
      moveLine(oldIndex, newIndex)
      recalculateSubtotals(true)
      trackInvoiceEditor('Form - Invoice lines - Invoice line reordered', {
        fromIndex: oldIndex,
        toIndex: newIndex,
      })
    },
    [moveLine, recalculateSubtotals, trackInvoiceEditor],
  )

  const handleDropdownOptionSelect = useCallback(
    (value: AddLineItemActionId) => {
      switch (value) {
        case AddLineItemActionId.AddDescription:
          handleAddDescriptionLineClick()
          break
        case AddLineItemActionId.AddSubtotal:
          handleAddSubtotalLineClick()
          break
      }
    },
    [handleAddDescriptionLineClick, handleAddSubtotalLineClick],
  )

  const dropdownOptions: NavItem<AddLineItemActionId>[] = useMemo(
    () => [
      {
        id: AddLineItemActionId.AddDescription,
        children: t('external_invoices.editor.form.field.invoice_lines.dropdown_options.add_text'),
        value: AddLineItemActionId.AddDescription,
        iconLeft: 'textSelect',
      },
      {
        id: AddLineItemActionId.AddSubtotal,
        children: t('external_invoices.editor.form.field.invoice_lines.dropdown_options.add_subtotal'),
        value: AddLineItemActionId.AddSubtotal,
        iconLeft: 'calculator',
      },
    ],
    [t],
  )

  useHotkeys(
    NEW_LINE_SHORTCUT,
    handleAddRowLineShortcutPressed,
    {
      enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'],
      enabled: !!defaultRowLine && !isDepositInvoice,
    },
    [defaultRowLine, isDepositInvoice],
  )

  if (isDepositInvoice) {
    return <DepositInvoiceLines />
  }

  return (
    <>
      <InvoiceLinesHeader />
      <DraggableItemList
        disabled={areInvoiceLinesDisabled}
        id="invoice-lines"
        isDraggable={!areInvoiceLinesDisabled}
        onOrderChange={handleLineOrderChange}
        showDragZoneOnlyOnHover
      >
        {lines.map((line, index) => {
          return (
            <InvoiceLine
              id={line.id}
              index={index}
              isRemovable={!areInvoiceLinesDisabled}
              key={line.id}
              lineType={line.type}
              onRemove={handleRemoveLineClick(index, line.type)}
            />
          )
        })}
      </DraggableItemList>
      <Space />

      {!areInvoiceLinesDisabled && (
        <Flex alignItems="center" gap={Spacing.S12}>
          <SplitButton
            icon="circlePlus"
            onClick={handleAddRowLineClick}
            dropdownOptions={dropdownOptions}
            onDropdownItemSelect={handleDropdownOptionSelect}
            tooltipLabel={t('external_invoices.editor.form.field.invoice_lines.add_line_tooltip', {
              shortcutKeys: NEW_LINE_SHORTCUT_KEYS_LABEL,
            })}
            tooltipPlacement="bottom"
            size="sm"
          >
            <Text variant="bodySM" weight="bold">
              {t(
                isQuote
                  ? 'external_invoices.editor.form.field.invoice_lines.add_quote_line'
                  : 'external_invoices.editor.form.field.invoice_lines.add_line',
              )}
            </Text>
          </SplitButton>
          <InvoiceEditLinesColumns />
          <InvoiceDiscounts />
        </Flex>
      )}
    </>
  )
}
